import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { I18n } from 'aws-amplify';
import { fetchAccountLicenses } from '../../components/util/callApi';
import { SOLAR_PRO_LICENSE_NAME } from '../../configures/val';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import styles from './styles.module.css';
import SelectBox from '../../components/common/SelectBox';
import Pagenation from '../../components/common/Pagenation';
import Button from '../../components/common/Button';
import linkIcon from '../../components/img/link_icon_white.svg';

const solarProUsageUrl = 'https://www.lapsys.co.jp/products/solarpro/portal/support.html';

const defaultPage = 0;
const maxLicensesPerPage = 10;

const defaultSelectedAccountId = -1;

/**
 * Solar Pro 画面
 * @param {Object} props
 * @param {Function} props.updateHeader - ヘッダの更新メソッド
 * @param {Object[]} props.accounts - アカウント情報
 * @returns {JSX.Element}
 */
const SolarPro = ({ updateHeader, accounts }) => {
  const breadcrumbsListItems = [
    {
      type: 'link',
      text: I18n.get('Service list'),
      to: '/servicelist',
    },
    {
      type: 'text',
      text: 'Solar Pro',
    },
  ];

  const updateHeaderRef = useRef(updateHeader);
  const [selectedAccountId, setSelectedAccountId] = useState(defaultSelectedAccountId);
  const [licenses, setLicenses] = useState([]);
  const [currentPage, setCurrentPage] = useState(defaultPage);

  /**
   * 選択されたアカウントもしくは全てのライセンス一覧
   */
  const filteredLicenses = useMemo(() => {
    if (selectedAccountId === defaultSelectedAccountId) return licenses;
    return licenses.filter((license) => license.account.id === selectedAccountId);
  }, [licenses, selectedAccountId]);

  /**
   * 現在のページで表示するライセンス一覧
   */
  const visibleLicenses = useMemo(() => {
    const start = currentPage * maxLicensesPerPage;
    return filteredLicenses.slice(start, start + maxLicensesPerPage);
  }, [currentPage, filteredLicenses]);

  /**
   * アカウント(契約者)の選択肢
   */
  const accountSelectBoxOptions = useMemo(() => {
    const accountsWithSolarProLicenses = licenses.map(({ account }) => account);
    if (accountsWithSolarProLicenses.length === 1)
      return [{ value: defaultSelectedAccountId, display: accountsWithSolarProLicenses[0].name }];

    return [{ value: defaultSelectedAccountId, display: I18n.get('All') }].concat(
      accountsWithSolarProLicenses.map(({ id, name }) => ({
        value: id,
        display: name,
      }))
    );
  }, [licenses]);

  /**
   * 所属するアカウントのライセンスを取得し状態変数にセットする
   */
  const loadLicenses = useCallback(async () => {
    try {
      const newLicenses = [];
      for await (const { id, displayName } of accounts.sort((a, b) => a.id - b.id)) {
        const { ok, data } = await fetchAccountLicenses(id);
        if (!ok) continue;
        const solarProLicenses = data.licenses.filter(
          ({ name }) => name === SOLAR_PRO_LICENSE_NAME
        );
        if (!solarProLicenses.length) continue;
        newLicenses.push({
          max: solarProLicenses.length,
          inUse: solarProLicenses.filter(({ user }) => Object.keys(user).length).length,
          account: {
            id: id,
            name: displayName,
          },
        });
      }
      setLicenses(newLicenses);
    } catch (error) {
      console.error('Failed to fetch licenses data', error);
    }
  }, [accounts]);

  // ヘッダを更新する
  useEffect(() => {
    if (updateHeaderRef.current) updateHeaderRef.current();
  }, []);

  useEffect(() => {
    loadLicenses();
  }, [loadLicenses]);

  // アカウント選択時に先頭ページに戻す
  useEffect(() => {
    setCurrentPage(defaultPage);
  }, [selectedAccountId]);

  if (!licenses.length) return <>{I18n.get('Now loading...')}</>;

  return (
    <div className={styles.container}>
      <Breadcrumbs listItems={breadcrumbsListItems} />
      <h1 className={styles.title}>
        {`${I18n.get('License Key')}${I18n.get('Usage Conditions')}`}
      </h1>
      <div className={styles.accountSelectArea}>
        <label htmlFor='account-select-box'>{I18n.get('Account(Subscriber)')}</label>
        <SelectBox
          name='account-select-box'
          options={accountSelectBoxOptions}
          onChange={(e) => setSelectedAccountId(Number(e.target.value))}
          value={selectedAccountId}
          scale='long'
        />
      </div>
      <div>
        <table className={styles.table}>
          <thead>
            <tr>
              <th rowSpan={2} className={styles.productNameHeader}>
                {I18n.get('Product Name')}
              </th>
              <th colSpan={2} className={styles.licenseKeyHeader}>
                {I18n.get('License Key')}
              </th>
              <th rowSpan={2} className={styles.accountNameHeader}>
                {I18n.get('Account(Subscriber)')}
              </th>
            </tr>
            <tr>
              <th>{I18n.get('Maximum Number of Simultaneous Uses')}</th>
              <th>{I18n.get('In Use')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleLicenses.map(({ max, inUse, account }) => (
              <tr>
                <td>{`Solar Pro ${I18n.get('Network Authentication Edition')}`}</td>
                <td className={styles.numberData}>{max}</td>
                <td className={styles.numberData}>{inUse}</td>
                <td className={styles.accountNameData}>
                  <div>{account.name}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredLicenses.length > maxLicensesPerPage && (
          <Pagenation
            totalPages={Math.ceil(filteredLicenses.length / maxLicensesPerPage)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </div>
      <div className={styles.supportInfoArea}>
        <p>{I18n.get('Support Information')}</p>
        <div className={styles.linkBtnArea}>
          <Button
            type='link'
            text={I18n.get('Installer Related Information')}
            size='large'
            linkTo='/servicelist/solarpro/installer-related-info'
          />
          <Button
            type='link'
            color='blueGray'
            text={
              <>
                {I18n.get('Learn how to use Solar Pro')}
                <img src={linkIcon} alt='' className={styles.linkIcon} />
              </>
            }
            size='large'
            linkType='external'
            linkTo={solarProUsageUrl}
            newTab={true}
          />
        </div>
      </div>
    </div>
  );
};

export default SolarPro;
